import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Google, Terminal, Visibility, VisibilityOff } from '@mui/icons-material';
import { supabase } from '../config';
import { EXTERNAL_LINKS, getDeveloperPortalOrigin } from '../constants/externalLinks';
import { useAuth } from '../contexts/AuthContext';
import {
  formatWaitTime,
  getLoginGateStatus,
  getSafeRedirectPath,
  registerBlockedLogin,
  registerFailedLoginAttempt,
  registerOAuthStart,
  registerSuccessfulLogin,
} from '../services/authSecurityService';

const SignupPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: 480,
  width: '100%',
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
}));

const GradientButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
  color: '#fff',
  padding: '14px 24px',
  borderRadius: 12,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
    boxShadow: '0 6px 20px rgba(143, 99, 255, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: 'linear-gradient(135deg, rgba(255, 139, 66, 0.5), rgba(143, 99, 255, 0.5))',
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  color: theme.palette.text.primary,
  border: '1px solid rgba(255, 255, 255, 0.12)',
  padding: '14px 24px',
  borderRadius: 12,
  fontWeight: 500,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const SignupPage: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const redirectPath = useMemo(() => {
    const stateFrom = (location.state as any)?.from;
    const fromPath = stateFrom?.pathname
      ? `${stateFrom.pathname}${stateFrom.search || ''}${stateFrom.hash || ''}`
      : null;
    const queryNext = new URLSearchParams(location.search).get('next');
    return getSafeRedirectPath(queryNext || fromPath || '/dashboard');
  }, [location.search, location.state]);

  useEffect(() => {
    if (user) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate, redirectPath]);

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password || !confirmPassword) {
      setError('Enter all required fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match. Please try again.");
      return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);

    const gateStatus = await getLoginGateStatus(normalizedEmail);
    if (!gateStatus.allowed) {
      setError(`Too many auth attempts. Try again in ${formatWaitTime(gateStatus.waitSeconds)}.`);
      await registerBlockedLogin(normalizedEmail, {
        method: 'signup_password',
        route: location.pathname,
        user_agent: navigator.userAgent,
        wait_seconds: gateStatus.waitSeconds,
      });
      setLoading(false);
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            account_type: 'developer',
            full_name: displayName.trim(),
            display_name: displayName.trim(),
          },
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      if (data.user) {
        await registerSuccessfulLogin(normalizedEmail, data.user.id, {
          method: 'signup_password',
          route: location.pathname,
          user_agent: navigator.userAgent,
          email_confirmed: Boolean(data.user.email_confirmed_at),
        });
      }

      if (data.session) {
        navigate(redirectPath, { replace: true });
      } else {
        setNotice('Account created. Check your email to verify your account before signing in.');
      }
    } catch (err: any) {
      await registerFailedLoginAttempt(normalizedEmail, {
        method: 'signup_password',
        route: location.pathname,
        user_agent: navigator.userAgent,
        reason: String(err?.message || 'unknown').slice(0, 180),
      });

      const nextGate = await getLoginGateStatus(normalizedEmail);
      if (!nextGate.allowed) {
        setError(`Too many auth attempts. Try again in ${formatWaitTime(nextGate.waitSeconds)}.`);
      } else {
        const remaining = nextGate.attemptsRemaining;
        const suffix = remaining <= 2 ? ` ${remaining} attempt(s) left before temporary lock.` : '';
        setError(`${err?.message || 'Failed to create account. Please try again.'}${suffix}`);
      }
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      if (normalizedEmail) {
        const gateStatus = await getLoginGateStatus(normalizedEmail);
        if (!gateStatus.allowed) {
          setError(`Too many auth attempts. Try again in ${formatWaitTime(gateStatus.waitSeconds)}.`);
          await registerBlockedLogin(normalizedEmail, {
            method: 'signup_google_oauth',
            route: location.pathname,
            user_agent: navigator.userAgent,
            wait_seconds: gateStatus.waitSeconds,
          });
          setLoading(false);
          return;
        }
      }

      await registerOAuthStart(normalizedEmail || undefined, {
        method: 'signup_google_oauth',
        route: location.pathname,
        user_agent: navigator.userAgent,
      });

      const origin = getDeveloperPortalOrigin();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${origin}/login?next=${encodeURIComponent(redirectPath)}`,
        },
      });

      if (oauthError) {
        throw oauthError;
      }
      setNotice('Redirecting to Google secure sign-up...');
    } catch (err: any) {
      if (normalizedEmail) {
        await registerFailedLoginAttempt(normalizedEmail, {
          method: 'signup_google_oauth',
          route: location.pathname,
          user_agent: navigator.userAgent,
          reason: String(err?.message || 'unknown').slice(0, 180),
        });
      }
      setError(err.message || 'Google signup failed. Please try again.');
      console.error('Google signup error:', err);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 30%, #15152a 0%, #05070a 60%)',
        padding: { xs: 2, sm: 4 },
        py: { xs: 4, sm: 0 },
      }}
    >
      <a href="#signup-form" className="skip-to-content">
        Skip to signup form
      </a>

      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box mb={4} textAlign="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Terminal
              sx={{
                fontSize: 40,
                color: '#ff8b42',
                filter: 'drop-shadow(0 0 8px rgba(255, 139, 66, 0.4))',
              }}
            />
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                ml: 1.5,
                color: '#fff',
                letterSpacing: '-0.02em',
              }}
            >
              Ad<span style={{ color: '#ff8b42' }}>Mesh</span>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                ml: 1.5,
                px: 1,
                py: 0.5,
                bgcolor: 'rgba(255, 139, 66, 0.15)',
                color: '#ff8b42',
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Dev
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: '#fff', letterSpacing: '-0.01em' }}>
            Join AdMesh Developer Network
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Create your account to start monetizing your games
          </Typography>
        </Box>

        <SignupPaper elevation={0} id="signup-form">
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}
            >
              {error}
            </Alert>
          )}
          {notice && (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: 'rgba(143, 99, 255, 0.12)',
                border: '1px solid rgba(143, 99, 255, 0.28)',
              }}
            >
              {notice}
            </Alert>
          )}

          <form onSubmit={handleSignup}>
            <TextField
              label="Display Name / Company"
              fullWidth
              margin="normal"
              variant="outlined"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={loading}
              autoComplete="name"
              autoFocus
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label="Email Address"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box mt={3} mb={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#ffae78',
                      },
                    }}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    I agree to the{' '}
                    <MuiLink href={EXTERNAL_LINKS.terms} target="_blank" underline="hover" sx={{ color: '#ffae78' }}>
                      Terms of Service
                    </MuiLink>
                    {' '}and{' '}
                    <MuiLink href={EXTERNAL_LINKS.privacy} target="_blank" underline="hover" sx={{ color: '#ffae78' }}>
                      Privacy Policy
                    </MuiLink>
                  </Typography>
                }
              />
            </Box>

            <GradientButton
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </GradientButton>
          </form>

          <Box my={3} display="flex" alignItems="center">
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.875rem' }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          </Box>

          <GoogleButton
            fullWidth
            size="large"
            startIcon={<Google />}
            onClick={handleGoogleSignup}
            disabled={loading}
          >
            Sign up with Google
          </GoogleButton>

          <Box mt={4} textAlign="center">
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Already have an account?{' '}
              <MuiLink
                component={Link}
                to="/login"
                underline="hover"
                sx={{
                  color: '#ffae78',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#ff8b42',
                  },
                }}
              >
                Sign in
              </MuiLink>
            </Typography>
          </Box>
        </SignupPaper>
      </Container>
    </Box>
  );
};

export default SignupPage;
